import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Agent } from "types";
import useAgents from "layouts/agent/agent-chat/hooks/useAgents";
import { useParams } from "react-router-dom";

interface AgentContextType {
  selectedAgent: Agent | null;
  setSelectedAgent: (agent: Agent | null) => void;
  availableAgents: Agent[];
  isLoadingAgents: boolean;
  agentsError: string | null;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

interface AgentProviderProps {
  children: ReactNode;
  isLogsAgentPage?: boolean;
}

export const AgentProvider: React.FC<AgentProviderProps> = ({
  children,
  isLogsAgentPage,
}) => {
  const { availableAgents, isLoadingAgents, agentsError } = useAgents({
    isLogsAgentPage,
  });
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const { agentId } = useParams<{ agentId?: string }>();

  const activeAgents = availableAgents.filter(
    (a) => !a.is_deleted && !a.is_archived
  );

  useEffect(() => {
    if (agentId && availableAgents.length > 0) {
      // If agentId is provided in URL, find and set that agent
      const agentFromUrl = availableAgents.find((a) => a.id === agentId);
      if (
        agentFromUrl &&
        !agentFromUrl.is_deleted &&
        !agentFromUrl.is_archived
      ) {
        setSelectedAgent(agentFromUrl);
        return;
      }
    }

    // If no agentId in URL or agent not found, set first available agent
    if (!selectedAgent && activeAgents.length > 0) {
      setSelectedAgent(activeAgents[0]);
    }
  }, [availableAgents, selectedAgent, activeAgents, agentId]);

  const value: AgentContextType = {
    selectedAgent,
    setSelectedAgent,
    availableAgents,
    isLoadingAgents,
    agentsError,
  };

  return (
    <AgentContext.Provider value={value}>{children}</AgentContext.Provider>
  );
};

export const useAgentContext = (): AgentContextType => {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error("useAgentContext must be used within an AgentProvider");
  }
  return context;
};
